import React, { Fragment, useRef, useState } from 'react';
import { RecordContextProvider, SimpleForm, useNotify, useRefresh, useTranslate } from 'react-admin';
import { useField } from 'react-final-form';
import { Button, Dialog, DialogContent, Step, StepLabel, Stepper } from '@material-ui/core'
import { Build } from '@material-ui/icons'
import moment from 'moment';

import { SURFACE_EVENT_TYPES } from '@hisports/common/src/constants';

import { DialogFormToolbar } from '../../../../common/dialogs/DialogForm';
import DialogTitleWithActions from '../../../../common/dialogs/DialogTitleWithActions';
import { apiClient } from '../../../../http';

import TemplateStep from './TemplateStep';
import RecurrenceStep from './RecurrenceStep';
import ResultsStep from './ResultsStep';

const STEPS = {
  TEMPLATE: 0,
  RECURRENCE: 1,
  RESULT: 2,
}

const formOptions = {
  destroyOnUnmount: true,
  enableReinitialize: false,
  forceUnregisterOnUnmount: true,
}

const validate = (step) => (values) => {
  if (step === 0) {
    const errors = {};

    if (!values.eventType) {
      errors.eventType = 'ra.validation.required';
    }
    if (!values.officeId) {
      errors.officeId = 'ra.validation.required';
    }
    if (!values.startTime) {
      errors.startTime = 'ra.validation.required';
    }
    if (!values.endTime) {
      errors.endTime = 'ra.validation.required';
    }
    if (!values.surfaceId) {
      errors.surfaceId = 'ra.validation.required';
    }
    if (values.duration != null && values.duration <= 0) {
      errors.duration = 'ra.validation.positive';
    }

    if (values.startTime && values.endTime) {
      if (!moment(values.startTime).isValid()) {
        errors.startTime = 'resources.arenaslots.validations.invalid_time'
      } else if (!moment(values.endTime).isValid()) {
        errors.endTime = 'resources.arenaslots.validations.invalid_time'
      } else if (moment(values.endTime).isBefore(values.startTime, 'minute')) {
        errors.endTime = 'resources.arenaslots.validations.end_before_start'
      }
    }

    return errors;
  } else if (step === 1) {
    if (!values.recurrenceDates?.length) {
      return {
        recurrenceDates: 'resources.arenaslots.validations.reccurences_dates_required',
      };
    }
  }
}

const TimeSlotStepper = ({ steps, activeStep }) => {
  return <Stepper activeStep={activeStep}>
    {steps.map((label) => (
      <Step key={label}>
        <StepLabel>{label}</StepLabel>
      </Step>
    ))}
  </Stepper>
}

const TimeSlotStep = ({ activeStep, setValidating, ...props }) => {
  switch (activeStep) {
    case STEPS.TEMPLATE:
      return <TemplateStep {...props} />
    case STEPS.RECURRENCE:
      return <RecurrenceStep setValidating={setValidating} {...props} />
    case STEPS.RESULT:
      return <ResultsStep {...props} />
  }
  return null;
}

const NextButton = ({ validating, handleNext }) => {
  const translate = useTranslate();
  const { input: { value: slots } } = useField('slots');

  return <Button color="primary" onClick={handleNext} disabled={!slots.length || validating}>
    {translate('ra.action.next')}
  </Button>
}

const SlotBuilderDialog = ({ isOpen, initialValues, onClose }) => {
  const [ activeStep, setActiveStep ] = useState(0);
  const [ validating, setValidating ] = useState(false);
  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  const stepRef = useRef()
  stepRef.current = activeStep; // necessary to use step in the callback

  const handleNext = () => {
    setActiveStep(activeStep => activeStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onClose();
      return;
    }
    setActiveStep(activeStep => activeStep - 1);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') return;

    setActiveStep(0);
    onClose();
  }

  const steps = [
    translate('resources.arenaslots.labels.stepper.template'),
    translate('resources.arenaslots.labels.stepper.recurrence'),
    translate('resources.arenaslots.labels.stepper.results'),
  ]

  const onSubmit = async (values) => {
    if (stepRef.current !== 2) return handleNext();

    const { slots, recurrenceDates, officeId, recurrenceDatesValid } = values;

    const validSlots = Object.keys(recurrenceDatesValid)
      .filter(date => recurrenceDates.includes(date))
      .flatMap(date => {
        const dateErrors = recurrenceDatesValid[date]?.errors || [];
        return slots
          .map(slot => {
            const hasError = dateErrors.some(err => err.slotId === slot.id);
            if (hasError) return null;
            return ({ ...slot, date })
          })
      })
      .filter(Boolean);

    if (!validSlots.length) {
      return;
    }

    return apiClient('/arenaslots/buildSlots', {
      method: 'POST',
      data: {
        slots: validSlots,
        dates: recurrenceDates,
        officeId
      }
    })
      .then(({ data }) => {
        const count = data?.count || 0;
        handleClose();
        notify(translate('resources.arenaslots.validations.build_slots_success', count), 'success');
        refresh();
      })
      .catch(() => {
        handleClose();
        notify('resources.arenaslots.validations.build_slots_error', 'error')
      })
  }

  const cancelLabel = activeStep === 0 ? "ra.action.cancel" : "ra.action.back";
  const submitLabel = activeStep === steps.length - 1 ? "ra.action.generate" : "ra.action.next";

  const toolbar = <DialogFormToolbar
    handleSubmit={onSubmit}
    onCancel={handleBack}
    cancelLabel={cancelLabel}
    submitLabel={submitLabel}
    hideSubmit={stepRef.current !== 2}
    extraButton={stepRef.current !== 2 && <NextButton validating={validating} handleNext={handleNext} />}
  />

  return <Dialog maxWidth="lg" fullWidth scroll="paper" open={isOpen} onClose={handleClose}>
    <DialogTitleWithActions onClose={handleClose}>
      {translate('resources.arenaslots.actions.build_slots')}
    </DialogTitleWithActions>
    <TimeSlotStepper steps={steps} activeStep={activeStep} />
    <DialogContent>
      <RecordContextProvider value={{}}>
        <SimpleForm
          {...formOptions}
          component={Fragment}
          validate={validate(activeStep)}
          initialValues={{ ...initialValues, name: translate('resources.arenaslots.name', 1), eventType: SURFACE_EVENT_TYPES.TIMESLOT, slots: [] }}
          save={onSubmit}
          toolbar={toolbar}
        >
          <TimeSlotStep activeStep={activeStep} setValidating={setValidating} />
        </SimpleForm>
      </RecordContextProvider>
    </DialogContent>
  </Dialog>
}

export const SlotBuilderButton = ({ size = 'small', variant, initialValues, disabled }) => {
  const [ isOpen, setOpen ] = useState(false);
  const translate = useTranslate();

  return <>
    <Button color="primary" size={size} variant={variant} startIcon={<Build />} onClick={() => setOpen(true)} disabled={disabled}>
      {translate('resources.arenaslots.actions.build_slots')}
    </Button>
    <SlotBuilderDialog isOpen={isOpen} onClose={() => setOpen(false)} initialValues={initialValues} />
  </>
}
